/**
 * Shared Tabulator editing behavior utilities (ES6 Module)
 * Used by modern components: DataGrid and TranslationsModal
 * 
 * Note: Legacy scripts like spreadsheet.js cannot import ES6 modules,
 * so they use inline implementations of these functions instead.
 */

/**
 * Normalizes a Tabulator range to ensure start is always top-left
 * and end is always bottom-right, regardless of selection direction
 * 
 * @param {Object} activeRange - The activeRange from table.modules.selectRange
 * @returns {Object} Normalized range with {startRow, endRow, startCol, endCol}
 */
function normalizeRange(activeRange) {
    const startRow = Math.min(activeRange.start.row, activeRange.end.row);
    const endRow = Math.max(activeRange.start.row, activeRange.end.row);
    const startCol = Math.min(activeRange.start.col, activeRange.end.col);
    const endCol = Math.max(activeRange.start.col, activeRange.end.col);
    
    return { startRow, endRow, startCol, endCol };
}

/**
 * Clears the currently selected range, using row.updateData() so that
 * Tabulator's history stack is NOT wiped. Captures a before-state snapshot
 * and pushes it to the table's clipboard history (table._clipboardHistory) so the clear
 * can be undone with Ctrl+Z via attachClipboardHistory.
 *
 * Returns the first affected cell (for afterCellClear callbacks), or null.
 *
 * @param {Tabulator} table
 */
function clearRangeWithHistory(table) {
    const selectRange = table.modules.selectRange;
    if (!selectRange?.activeRange) return null;

    const { startRow, endRow, startCol, endCol } = normalizeRange(selectRange.activeRange);
    const columns = table.getColumns();

    const snapshot = [];
    const updates = [];

    for (let rowIdx = startRow; rowIdx <= endRow; rowIdx++) {
        const row = table.getRowFromPosition(rowIdx + 1);
        if (!row) continue;
        const before = {};
        const clear = {};
        for (let colIdx = startCol; colIdx <= endCol; colIdx++) {
            const column = columns[colIdx];
            if (!column) continue;
            const def = column.getDefinition();
            if (def.editor !== false && def.type !== 'form' && def.field !== '_title') {
                before[def.field] = row.getCell(column)?.getValue() ?? null;
                clear[def.field] = '';
            }
        }
        if (Object.keys(clear).length > 0) {
            snapshot.push({ row, before });
            updates.push({ row, data: clear });
        }
    }

    if (updates.length === 0) return null;

    table.blockRedraw();
    try {
        updates.forEach(({ row, data }) => row.update(data));
    } finally {
        table.restoreRedraw();
    }

    // Push to clipboard history so Ctrl+Z can undo the clear atomically
    table._clipboardHistory?.push(snapshot);

    const firstRow = table.getRowFromPosition(startRow + 1);
    return firstRow?.getCell(columns[startCol]) ?? null;
}

/**
 * Extends Tabulator's keybindings module with a clearCell action
 * that respects edit mode, tracks changes in undo history, and doesn't
 * wipe Tabulator's own history stack (unlike cell.setValue()).
 */
export function extendTabulatorClearCell(TabulatorClass) {
    if (typeof TabulatorClass === 'undefined') {
        console.warn('Tabulator not available for clearCell extension');
        return;
    }

    TabulatorClass.extendModule('keybindings', 'actions', {
        clearCell: function() {
            if (this.table.modules.edit?.currentCell) return;

            const firstCell = clearRangeWithHistory(this.table);
            if (firstCell && this.table.options.afterCellClear) {
                this.table.options.afterCellClear(firstCell);
            }
        }
    });
}

/**
 * Sets up editing behavior for Tabulator table:
 * - Prevents arrow keys and backspace from navigating away during editing
 * - Allows cursor movement within text
 * 
 * @param {Tabulator} table - The Tabulator instance
 * @param {Object} options - Configuration options
 * @param {Function} options.additionalKeyHandler - Optional additional key handler for custom behavior
 */
export function setupCellEditingBehavior(table, options = {}) {
    table.on('cellEditing', function(cell) {
        const cellElement = cell.getElement();
        
        const cellKeyHandler = function(e) {
            if (e.key.startsWith('Arrow') || e.key === 'Backspace') {
                e.stopPropagation();
            }
            
            if (options.additionalKeyHandler) {
                options.additionalKeyHandler(e, cell);
            }
        };
        
        cellElement.addEventListener('keydown', cellKeyHandler);
        
        const cleanupOnce = function() {
            cellElement.removeEventListener('keydown', cellKeyHandler);
            table.off('cellEditCancelled', cleanupOnce);
            table.off('cellEdited', cleanupOnce);
        };
        
        table.on('cellEditCancelled', cleanupOnce);
        table.on('cellEdited', cleanupOnce);
    });
}

/**
 * Sets up "type to edit" behavior - start editing when typing any printable character
 * 
 * @param {Tabulator} table - The Tabulator instance
 * @param {Object} options - Configuration options
 * @param {Function} options.isEditableCell - Optional function to determine if cell is editable
 */
export function setupTypeToEdit(table, options = {}) {
    const tableElement = table.element || table;

    const handleKeydown = (e) => {
        if (table.modules.edit && table.modules.edit.currentCell) return;

        const selectRange = table.modules.selectRange;
        if (!selectRange || !selectRange.activeRange) return;

        const isPrintable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

        if (isPrintable) {
            const activeRange = selectRange.activeRange;
            const columns = table.getColumns();
            const row = table.getRowFromPosition(activeRange.start.row + 1);
            const column = columns[activeRange.start.col];

            if (row && column) {
                const cell = row.getCell(column);
                const columnDef = column.getDefinition();

                const isEditable = options.isEditableCell
                    ? options.isEditableCell(columnDef, cell)
                    : (columnDef.editor !== false && columnDef.type !== 'form' && columnDef.field !== '_title');

                if (isEditable) {
                    e.preventDefault();
                    cell.edit();
                    setTimeout(() => {
                        const editor = cell.getElement().querySelector('input, textarea');
                        if (editor) {
                            editor.value = editor.value + e.key;
                            editor.selectionStart = editor.selectionEnd = editor.value.length;
                        }
                    }, 10);
                }
            }
        }
    };

    tableElement.addEventListener('keydown', handleKeydown);
    return () => tableElement.removeEventListener('keydown', handleKeydown);
}

/**
 * Sets up auto-scroll behavior when cells are selected via keyboard navigation
 * Ensures selected cells are always visible in the viewport with smooth scrolling
 * when approaching edges
 * 
 * @param {Tabulator} table - The Tabulator instance
 * @param {Object} options - Configuration options
 * @param {number} options.edgeThreshold - Pixels from edge to trigger scroll (default: 50)
 */
export function setupAutoScroll(table, options = {}) {
    const edgeThreshold = options.edgeThreshold || 30;
    const maxScrollSpeed = options.maxScrollSpeed || 12;
    const minScrollSpeed = options.minScrollSpeed || 3;
    let isDragging = false;
    let animationFrame = null;
    let lastMouseEvent = null;
    
    const performScroll = () => {
        if (!isDragging || !lastMouseEvent) {
            animationFrame = null;
            return;
        }
        
        const tableHolder = table.element.querySelector('.tabulator-tableholder');
        if (!tableHolder) {
            animationFrame = null;
            return;
        }
        
        const containerRect = tableHolder.getBoundingClientRect();
        const mouseX = lastMouseEvent.clientX;
        const mouseY = lastMouseEvent.clientY;
        
        const topDistance = mouseY - containerRect.top;
        const bottomDistance = containerRect.bottom - mouseY;
        const leftDistance = mouseX - containerRect.left;
        const rightDistance = containerRect.right - mouseX;
        
        let scrolled = false;
        
        if (topDistance < edgeThreshold && topDistance > 0) {
            const ratio = 1 - (topDistance / edgeThreshold);
            const speed = minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * ratio;
            tableHolder.scrollTop -= speed;
            scrolled = true;
        } else if (bottomDistance < edgeThreshold && bottomDistance > 0) {
            const ratio = 1 - (bottomDistance / edgeThreshold);
            const speed = minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * ratio;
            tableHolder.scrollTop += speed;
            scrolled = true;
        }
        
        if (leftDistance < edgeThreshold && leftDistance > 0) {
            const ratio = 1 - (leftDistance / edgeThreshold);
            const speed = minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * ratio;
            tableHolder.scrollLeft -= speed;
            scrolled = true;
        } else if (rightDistance < edgeThreshold && rightDistance > 0) {
            const ratio = 1 - (rightDistance / edgeThreshold);
            const speed = minScrollSpeed + (maxScrollSpeed - minScrollSpeed) * ratio;
            tableHolder.scrollLeft += speed;
            scrolled = true;
        }
        
        if (scrolled) {
            animationFrame = requestAnimationFrame(performScroll);
        } else {
            animationFrame = null;
        }
    };
    
    const handleDragScroll = (e) => {
        if (!isDragging) return;
        
        lastMouseEvent = e;
        
        if (!animationFrame) {
            animationFrame = requestAnimationFrame(performScroll);
        }
    };

    const handleMouseUp = () => {
        isDragging = false;
        lastMouseEvent = null;
        if (animationFrame) {
            cancelAnimationFrame(animationFrame);
            animationFrame = null;
        }
    };
    
    const handleMouseDown = (e) => {
        if (e.target.closest('.tabulator-cell')) isDragging = true;
    };

    table.element.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleDragScroll);
    document.addEventListener('mouseup', handleMouseUp);

    table.on('rangeSelected', function(range) {
        if (!range || range.length === 0) return;
        
        const firstCell = range[0];
        if (!firstCell || !firstCell.row || !firstCell.column) return;
        
        try {
            const cell = firstCell.row.getCell(firstCell.column);
            const cellElement = cell.getElement();
            
            if (!cellElement) return;
            
            const tableHolder = table.element.querySelector('.tabulator-tableholder');
            if (!tableHolder) {
                cellElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest'
                });
                return;
            }
            
            const cellRect = cellElement.getBoundingClientRect();
            const containerRect = tableHolder.getBoundingClientRect();
            
            const topDistance = cellRect.top - containerRect.top;
            const bottomDistance = containerRect.bottom - cellRect.bottom;
            const leftDistance = cellRect.left - containerRect.left;
            const rightDistance = containerRect.right - cellRect.right;
            
            if (topDistance < edgeThreshold) {
                tableHolder.scrollTop -= (edgeThreshold - topDistance);
            } else if (bottomDistance < edgeThreshold) {
                tableHolder.scrollTop += (edgeThreshold - bottomDistance);
            }
            
            if (leftDistance < edgeThreshold) {
                tableHolder.scrollLeft -= (edgeThreshold - leftDistance);
            } else if (rightDistance < edgeThreshold) {
                tableHolder.scrollLeft += (edgeThreshold - rightDistance);
            }
        } catch (e) {}
    });

    return () => {
        table.element.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleDragScroll);
        document.removeEventListener('mouseup', handleMouseUp);
        if (animationFrame) cancelAnimationFrame(animationFrame);
    };
}

/**
 * Sets up Excel-like context menu for Tabulator tables.
 * Provides Undo, Cut, Copy, Paste, and Clear options.
 *
 * @param {Tabulator} table - The Tabulator instance
 * @param {Object} options
 * @param {Function} options.canUndo - Returns true when there is something to undo
 * @param {Function} options.onUndo  - Called when Undo is selected from the menu
 */
export function setupContextMenu(table, { canUndo, onUndo, canRedo, onRedo } = {}) {
    let contextMenu = null;

    const isMac = navigator.userAgent.includes('Mac');
    const modKey = isMac ? '⌘' : 'Ctrl';

    const buildMenuItems = () => [
        ...(onUndo ? [{ label: 'Undo', action: 'undo', shortcut: `${modKey}+Z`, isDisabled: () => !canUndo?.() }] : []),
        ...(onRedo ? [{ label: 'Redo', action: 'redo', shortcut: `${modKey}+Y`, isDisabled: () => !canRedo?.() }] : []),
        { type: 'separator', showOnlyIf: () => !!(onUndo || onRedo) },
        { label: 'Cut',   action: 'cut',   shortcut: `${modKey}+X` },
        { label: 'Copy',  action: 'copy',  shortcut: `${modKey}+C` },
        { label: 'Paste', action: 'paste', shortcut: `${modKey}+V` },
        { type: 'separator' },
        { label: 'Clear', action: 'clear', shortcut: 'Del' },
    ];

    const createContextMenu = () => {
        const menu = document.createElement('div');
        menu.className = 'tabulator-context-menu';
        menu.style.cssText = `
            position: fixed;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            z-index: 10000;
            padding: 4px 0;
            min-width: 140px;
            display: none;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            font-size: 13px;
        `;
        document.body.appendChild(menu);
        return menu;
    };

    const renderMenuItems = () => {
        contextMenu.innerHTML = '';
        buildMenuItems().forEach(item => {
            if (item.type === 'separator') {
                if (item.showOnlyIf && !item.showOnlyIf()) return;
                const sep = document.createElement('div');
                sep.style.cssText = 'height: 1px; background: #e0e0e0; margin: 4px 0;';
                contextMenu.appendChild(sep);
                return;
            }

            const disabled = item.isDisabled?.() ?? false;
            const menuItem = document.createElement('div');
            menuItem.className = 'tabulator-context-menu-item';
            menuItem.style.cssText = `
                padding: 6px 12px;
                cursor: ${disabled ? 'default' : 'pointer'};
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 24px;
                opacity: ${disabled ? '0.4' : '1'};
            `;

            const labelSpan = document.createElement('span');
            labelSpan.textContent = item.label;

            const shortcutSpan = document.createElement('span');
            shortcutSpan.textContent = item.shortcut;
            shortcutSpan.style.cssText = 'color: #999; font-size: 0.9em;';

            menuItem.appendChild(labelSpan);
            menuItem.appendChild(shortcutSpan);

            if (!disabled) {
                menuItem.addEventListener('mouseenter', () => { menuItem.style.background = '#f5f5f5'; });
                menuItem.addEventListener('mouseleave', () => { menuItem.style.background = 'white'; });
                menuItem.addEventListener('click', (e) => {
                    e.preventDefault();
                    executeAction(item.action);
                    hideContextMenu();
                });
            }
            contextMenu.appendChild(menuItem);
        });
    };
    
    const showContextMenu = (x, y) => {
        if (!contextMenu) contextMenu = createContextMenu();
        renderMenuItems();
        contextMenu.style.left = x + 'px';
        contextMenu.style.top = y + 'px';
        contextMenu.style.display = 'block';
    };
    
    const hideContextMenu = () => {
        if (contextMenu) contextMenu.style.display = 'none';
    };

    const clearSelectedCells = () => {
        const firstCell = clearRangeWithHistory(table);
        if (firstCell && table.options.afterCellClear) {
            table.options.afterCellClear(firstCell);
        }
    };
    
    const executeAction = async (action) => {
        switch (action) {
            case 'undo':
                onUndo?.();
                break;
            case 'redo':
                onRedo?.();
                break;
            case 'cut':
                if (table.modules.selectRange?.activeRange && table.modules.clipboard) {
                    table.modules.clipboard.copy('range', true);
                    clearSelectedCells();
                }
                break;
            case 'copy':
                if (table.modules.selectRange?.activeRange && table.modules.clipboard) {
                    table.modules.clipboard.copy('range', true);
                }
                break;
            case 'paste':
                if (navigator.clipboard?.readText) {
                    try {
                        const text = await navigator.clipboard.readText();
                        if (text && table.modules.clipboard) {
                            const dataTransfer = new DataTransfer();
                            dataTransfer.setData('text/plain', text);
                            table.element.dispatchEvent(new ClipboardEvent('paste', {
                                bubbles: true,
                                cancelable: true,
                                clipboardData: dataTransfer,
                            }));
                        }
                    } catch (err) {
                        console.warn('Clipboard read failed:', err);
                        document.execCommand('paste');
                    }
                } else {
                    document.execCommand('paste');
                }
                break;
            case 'clear':
                clearSelectedCells();
                break;
        }
    };
    
    const tableElement = table.element;

    const handleContextMenu = (e) => {
        if (!e.target.closest('.tabulator-cell')) return;
        e.preventDefault();
        showContextMenu(e.clientX, e.clientY);
    };

    const handleDocumentClick = (e) => {
        if (contextMenu && !contextMenu.contains(e.target)) hideContextMenu();
    };

    const handleScroll = () => hideContextMenu();

    const handleKeydown = (e) => {
        if (table.modules.edit?.currentCell) return;
        const mod = navigator.userAgent.includes('Mac') ? e.metaKey : e.ctrlKey;
        if (mod && e.key === 'c') { e.preventDefault(); executeAction('copy'); }
        else if (mod && e.key === 'x') { e.preventDefault(); executeAction('cut'); }
        else if (mod && e.key === 'v') { e.preventDefault(); executeAction('paste'); }
        else if (mod && e.key === 'y') { e.preventDefault(); executeAction('redo'); }
    };

    tableElement.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleDocumentClick);
    tableElement.addEventListener('scroll', handleScroll);
    tableElement.addEventListener('keydown', handleKeydown);

    return () => {
        tableElement.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('click', handleDocumentClick);
        tableElement.removeEventListener('scroll', handleScroll);
        tableElement.removeEventListener('keydown', handleKeydown);
        contextMenu?.remove();
        contextMenu = null;
    };
}

/**
 * Creates a per-table clipboard history that tracks paste and clear operations
 * as atomic before-state snapshots — separate from Tabulator's native cell-edit history.
 *
 * The undo and redo stacks are kept in sync: committing a new action (push) always
 * clears the redo stack, matching standard undo/redo behaviour. pushUndoOnly is a
 * private escape hatch used by redo to re-enable undo without discarding the redo stack.
 *
 * Call this before new Tabulator() and pass the result to makeClipboardPasteAction()
 * and attachClipboardHistory().
 *
 * @returns {{
 *   push:         (snapshot) => void,  // commit new action — clears redo stack
 *   pop:          ()         => snapshot,
 *   canUndo:      ()         => boolean,
 *   pushUndoOnly: (snapshot) => void,  // push to undo without clearing redo (redo internals)
 *   pushRedo:     (snapshot) => void,
 *   popRedo:      ()         => snapshot,
 *   canRedo:      ()         => boolean,
 *   clearRedo:    ()         => void,  // discard redo stack only (used when new actions commit)
 *   clear:        ()         => void,
 * }}
 */
export function makeClipboardHistory() {
    const stack = [];
    const redoStack = [];
    return {
        push:         (snapshot) => { stack.push(snapshot); redoStack.length = 0; },
        pop:          ()         => stack.pop(),
        canUndo:      ()         => stack.length > 0,
        // Push to undo stack without clearing redo — used when redo re-enables undo
        pushUndoOnly: (snapshot) => stack.push(snapshot),
        pushRedo:     (snapshot) => redoStack.push(snapshot),
        popRedo:      ()         => redoStack.pop(),
        canRedo:      ()         => redoStack.length > 0,
        clearRedo:    ()         => { redoStack.length = 0; },
        clear:        ()         => { stack.length = 0; redoStack.length = 0; },
    };
}

/**
 * Returns a clipboardPasteAction function that:
 *  1. Captures a before-state snapshot of the affected range
 *  2. Executes the paste using row.update() — this does NOT clear Tabulator's
 *     cell-edit history (unlike cell.setValue() which clears it on every call)
 *  3. Commits the snapshot to the clipboard history (clearing the redo stack)
 *
 * Workaround for https://github.com/olifolkerd/tabulator/issues/4398.
 * Undo/redo for paste is handled separately by attachClipboardHistory().
 *
 * @param {Object} history - Instance returned by makeClipboardHistory()
 */
export function makeClipboardPasteAction(history) {
    return function pasteIntoRange(data) {
        const range = this.table.modules.selectRange?.activeRange;
        if (!range) return [];

        const bounds = range.getBounds();
        const startCell = bounds?.start;
        if (!startCell) return [];

        // Use internal activeRows only to locate the range bounds by reference.
        // Then convert to RowComponents (public API) so snapshots are consistent
        // with clearRangeWithHistory and undo always calls row.update().
        const internalRows = this.table.rowManager.activeRows.slice();
        const startRow = internalRows.indexOf(startCell.row);
        if (startRow === -1) return [];

        const dataLength = data.length;
        const rowWidth = bounds.start === bounds.end
            ? dataLength
            : internalRows.indexOf(bounds.end.row) - startRow + 1;

        const affected = internalRows
            .slice(startRow, startRow + rowWidth)
            .map(r => r.getComponent());

        const fields = dataLength > 0 ? Object.keys(data[0]) : [];

        // Capture before-state so the paste can be undone atomically
        const snapshot = affected.map(row => ({
            row,
            before: Object.fromEntries(
                fields.map(field => [field, row.getCell(field)?.getValue() ?? null])
            ),
        }));

        this.table.blockRedraw();
        try {
            affected.forEach((row, i) => row.update(data[i % dataLength]));
        } finally {
            this.table.restoreRedraw();
        }

        history?.push(snapshot);
        return affected;
    };
}

/**
 * Attaches Ctrl+Z / Ctrl+Y (Ctrl+Shift+Z) handlers that undo and redo paste and
 * clear operations atomically, then fall back to Tabulator's native cell-edit history.
 *
 * Undo priority:
 *   1. Clipboard history stack (paste / clear snapshots) — atomic row-level restore
 *   2. Tabulator's native undo (cell-by-cell edits via table.undo())
 *
 * Redo priority mirrors undo:
 *   1. Clipboard history redo stack
 *   2. Tabulator's native redo (table.redo())
 *
 * Ctrl+Z is intercepted in capture phase only when the clipboard history has items,
 * so Tabulator's bubble-phase keybinding still handles cell-edit undo when the
 * clipboard history is empty. Ctrl+Y / Ctrl+Shift+Z are always intercepted since
 * Tabulator has no native redo keybinding.
 *
 * Call this after new Tabulator() is created.
 *
 * @param {Tabulator} table
 * @param {Object} history - Instance returned by makeClipboardHistory()
 * @param {Object} options
 * @param {Function} options.afterRestore - Called after every undo/redo to sync external state
 * @returns {{ canUndo, canRedo, undo, redo, cleanup }}
 */
export function attachClipboardHistory(table, history, { afterRestore } = {}) {
    const canUndo = () => history.canUndo() || table.getHistoryUndoSize() > 0;
    const canRedo = () => history.canRedo() || table.getHistoryRedoSize() > 0;

    // Reads the fields named in `snapshot` from the live rows — used to capture
    // the current state before it is overwritten, so the inverse operation can
    // be pushed onto the opposite stack.
    const captureCurrentValues = (snapshot) =>
        snapshot.map(({ row, before }) => ({
            row,
            before: Object.fromEntries(
                Object.keys(before).map(field => [field, row.getCell(field)?.getValue() ?? null])
            ),
        }));

    const undo = () => {
        if (history.canUndo()) {
            const snapshot = history.pop();
            // Capture current state (post-paste/clear) so redo can reapply it
            const redoSnapshot = captureCurrentValues(snapshot);
            table.blockRedraw();
            try {
                snapshot.forEach(({ row, before }) => row.update(before));
                // Push to redo only after all rows succeed — keeps stacks consistent.
                history.pushRedo(redoSnapshot);
            } catch (e) {
                // Roll back: restore the undo entry so the action isn't silently lost.
                history.pushUndoOnly(snapshot);
                throw e;
            } finally {
                table.restoreRedraw();
            }
            afterRestore?.();
        } else if (table.getHistoryUndoSize() > 0) {
            table.undo();
            afterRestore?.();
        }
    };

    const redo = () => {
        if (history.canRedo()) {
            const snapshot = history.popRedo();
            // Capture current state (pre-redo) so undo can revert it again
            const undoSnapshot = captureCurrentValues(snapshot);
            table.blockRedraw();
            try {
                snapshot.forEach(({ row, before }) => row.update(before));
                // Push to undo only after all rows succeed — keeps stacks consistent.
                history.pushUndoOnly(undoSnapshot);
            } catch (e) {
                // Roll back: restore the redo entry so the action isn't silently lost.
                history.pushRedo(snapshot);
                throw e;
            } finally {
                table.restoreRedraw();
            }
            afterRestore?.();
        } else if (table.getHistoryRedoSize() > 0) {
            table.redo();
            afterRestore?.();
        }
    };

    // Capture phase so we intercept before Tabulator's bubble-phase keybindings.
    // For Ctrl+Z: only intercept when clipboard history has items; let Tabulator handle
    // cell-edit undo natively (its keybindings module fires in bubble phase).
    // For Ctrl+Y / Ctrl+Shift+Z: always intercept since Tabulator has no native binding.
    const handleKeydown = (e) => {
        if (table.modules.edit?.currentCell) return;
        const mod = navigator.userAgent.includes('Mac') ? e.metaKey : e.ctrlKey;
        if (mod && e.key === 'z' && !e.shiftKey && history.canUndo()) {
            e.preventDefault();
            e.stopPropagation();
            undo();
        } else if (mod && (e.key === 'y' || (e.key === 'z' && e.shiftKey)) && canRedo()) {
            e.preventDefault();
            e.stopPropagation();
            redo();
        }
    };

    // Any committed cell edit is a new action — discard clipboard redo so that
    // Ctrl+Y cannot replay a past paste after the user has typed something new.
    // Mirrors the behaviour of history.push() which clears redo on paste/clear.
    const handleCellEdited = () => history.clearRedo();

    table.element.addEventListener('keydown', handleKeydown, true);
    table.on('cellEdited', handleCellEdited);

    return {
        canUndo,
        canRedo,
        undo,
        redo,
        cleanup: () => {
            table.element.removeEventListener('keydown', handleKeydown, true);
            table.off('cellEdited', handleCellEdited);
        },
    };
}

/**
 * Patches both the Tabulator root element and the tableholder (.tabulator-tableholder)
 * to always call focus() with preventScroll:true.
 *
 * Without this, SelectRange.restoreFocus() and handleCellClick both call
 * this.table.rowManager.element.focus() which causes the browser to scroll the nearest
 * scroll ancestor (typically the modal) a few pixels on the first cell click,
 * visually displacing the sticky header.
 *
 * Call inside the 'tableBuilt' event handler.
 *
 * @param {Object} table - Tabulator instance
 */
export function preventFocusScroll(table) {
    [table.element, table.rowManager?.element].forEach(el => {
        if (!el) return;
        const orig = el.focus.bind(el);
        el.focus = (opts) => orig({ preventScroll: true, ...opts });
    });
}

/**
 * Removes the initial visual range selection that Tabulator creates when
 * selectableRangeAutoFocus:false is set. Without this the first cell appears
 * highlighted (as if selected) but the table has no browser focus, so
 * Ctrl+V paste silently does nothing — a confusing mismatch for users.
 *
 * Call this inside the 'tableBuilt' event handler.
 *
 * @param {Object} table - Tabulator instance
 */
export function clearInitialRangeSelection(table) {
    const selMod = table.modules?.selectRange;
    if (selMod && Array.isArray(selMod.ranges)) {
        selMod.ranges.forEach(r => r.destroy());
        selMod.ranges = [];
    }
}

/**
 * Recommended Tabulator configuration for consistent editing behavior.
 * Spread this into every new Tabulator() call and add component-specific
 * overrides (layout, columns, height, clipboardPasteAction, afterCellClear…) afterwards.
 *
 * clipboardPasteAction is intentionally absent: each call site creates its own
 * clipboard history via makeClipboardHistory() and passes makeClipboardPasteAction(history).
 */
export const RECOMMENDED_TABULATOR_CONFIG = {
    clipboard: true,
    clipboardCopyConfig: {
        columnHeaders: false,
        rowHeaders: false,
    },
    clipboardCopyRowRange: 'range',
    clipboardPasteParser: 'range',
    history: true,
    selectableRange: 1,
    selectableRangeAutoFocus: false,
    selectableRangeClearCellsValue: '',
    editTriggerEvent: 'dblclick',
    keybindings: {
        clearCell: ['8', '46'], // Backspace and Delete
    },
};

